import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { 
  fetchTeamDetails, 
  updateTeam, 
  deleteTeam, 
  leaveTeam,
  inviteMember,
  fetchPendingInvites,
  cancelInvite
} from "../../services/teamsService";
import { 
  Users, 
  ArrowLeft,
  Loader2,
  Target,
  Crown,
  Settings,
  UserPlus,
  Trash2,
  LogOut,
  Check,
  X,
  Edit2,
  Calendar,
  Mail,
  Clock,
  Send
} from "lucide-react";
import DashboardNav from "../../components/DashboardNav";
import TeamSignalDashboard from "../../components/teams/TeamSignalDashboard";

const TeamDetailPage = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalValue, setGoalValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  
  // Invite state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteMessage, setInviteMessage] = useState("");
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [pendingInvites, setPendingInvites] = useState([]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    const loadTeam = async () => {
      try {
        const data = await fetchTeamDetails(teamId);
        setTeam(data);
        setGoalValue(data.quarterly_goal || "");
      } catch (err) {
        if (err.message === 'Unauthorized') {
          navigate('/login', { replace: true });
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && teamId) {
      loadTeam();
      loadPendingInvites();
    }
  }, [isAuthenticated, teamId, navigate]);

  const loadPendingInvites = async () => {
    try {
      const invites = await fetchPendingInvites(teamId);
      setPendingInvites(invites);
    } catch (err) {
      // Silently fail - user might not be admin
    }
  };

  const handleSaveGoal = async () => {
    setSaving(true);
    try {
      const updated = await updateTeam(teamId, { quarterly_goal: goalValue });
      setTeam(updated);
      setEditingGoal(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTeam = async () => {
    setSaving(true);
    try {
      await deleteTeam(teamId);
      navigate('/dashboard/teams');
    } catch (err) {
      setError(err.message);
      setShowDeleteConfirm(false);
    } finally {
      setSaving(false);
    }
  };

  const handleLeaveTeam = async () => {
    setSaving(true);
    try {
      await leaveTeam(teamId);
      navigate('/dashboard/teams');
    } catch (err) {
      setError(err.message);
      setShowLeaveConfirm(false);
    } finally {
      setSaving(false);
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    setInviteSending(true);
    setInviteError("");
    
    try {
      await inviteMember(teamId, {
        email: inviteEmail,
        name: inviteName || null,
        message: inviteMessage || null
      });
      setInviteSuccess(true);
      loadPendingInvites();
      
      // Reset form after success
      setTimeout(() => {
        setShowInviteModal(false);
        setInviteSuccess(false);
        setInviteEmail("");
        setInviteName("");
        setInviteMessage("");
      }, 2000);
    } catch (err) {
      setInviteError(err.message);
    } finally {
      setInviteSending(false);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await cancelInvite(teamId, inviteId);
      loadPendingInvites();
    } catch (err) {
      setError(err.message);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  if (error && !team) {
    return (
      <div className="min-h-screen bg-[#1a1a1a]">
        <DashboardNav />
        <main className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center py-16">
            <p className="text-red-400 mb-4">{error}</p>
            <button
              onClick={() => navigate('/dashboard/teams')}
              className="text-red-400 hover:text-red-300"
            >
              ← Back to Teams
            </button>
          </div>
        </main>
      </div>
    );
  }

  const isAdmin = team?.my_role === 'admin';
  const isCreator = team?.created_by === user?.user_id;

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      <DashboardNav />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Link */}
        <button
          onClick={() => navigate('/dashboard/teams')}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Teams
        </button>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
            {error}
          </div>
        )}

        {/* Team Header */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-red-500/20 to-purple-500/20 rounded-xl flex items-center justify-center">
                <Users className="w-7 h-7 text-red-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">{team?.name}</h1>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-zinc-400">
                    {team?.members?.length} member{team?.members?.length !== 1 ? 's' : ''}
                  </span>
                  {isAdmin && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-500/10 rounded-full text-amber-400 text-xs">
                      <Crown className="w-3 h-3" />
                      Admin
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-2">
              {isAdmin && (
                <button
                  onClick={() => setShowInviteModal(true)}
                  data-testid="invite-member-btn"
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <UserPlus className="w-4 h-4" />
                  Invite
                </button>
              )}
              <button
                className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>

          {team?.description && (
            <p className="mt-4 text-zinc-400">{team.description}</p>
          )}
        </div>

        {/* Quarterly Goal */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-zinc-300">
              <Target className="w-5 h-5 text-red-400" />
              <span className="font-medium">Quarterly Goal</span>
            </div>
            {isAdmin && !editingGoal && (
              <button
                onClick={() => setEditingGoal(true)}
                className="text-zinc-400 hover:text-white p-1"
              >
                <Edit2 className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {editingGoal ? (
            <div className="space-y-3">
              <textarea
                value={goalValue}
                onChange={(e) => setGoalValue(e.target.value)}
                placeholder="e.g., Launch MVP and get 100 active users"
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                rows={2}
                maxLength={500}
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSaveGoal}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white rounded-lg transition-colors"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Save
                </button>
                <button
                  onClick={() => {
                    setEditingGoal(false);
                    setGoalValue(team?.quarterly_goal || "");
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <p className={team?.quarterly_goal ? "text-white" : "text-zinc-500 italic"}>
              {team?.quarterly_goal || "No goal set yet"}
            </p>
          )}
        </div>

        {/* Members */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6 mb-6">
          <h2 className="text-lg font-medium text-white mb-4">Team Members</h2>
          <div className="space-y-3">
            {team?.members?.map((member) => (
              <div 
                key={member.user_id}
                className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-zinc-700 rounded-full flex items-center justify-center">
                    <span className="text-white font-medium">
                      {(member.name || member.email || '?').charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-white font-medium">
                      {member.name || member.email}
                    </p>
                    <p className="text-sm text-zinc-500">{member.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {member.role === 'admin' && (
                    <span className="flex items-center gap-1 px-2 py-1 bg-amber-500/10 rounded text-amber-400 text-xs">
                      <Crown className="w-3 h-3" />
                      Admin
                    </span>
                  )}
                  <span className="text-xs text-zinc-500 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Joined {new Date(member.joined_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pending Invites */}
        {isAdmin && pendingInvites.length > 0 && (
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6 mb-6">
            <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-zinc-400" />
              Pending Invites
            </h2>
            <div className="space-y-3">
              {pendingInvites.map((invite) => (
                <div 
                  key={invite.invite_id}
                  className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg"
                >
                  <div>
                    <p className="text-white">{invite.email}</p>
                    <p className="text-xs text-zinc-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Expires {new Date(invite.expires_at).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => handleCancelInvite(invite.invite_id)}
                    className="p-2 text-zinc-400 hover:text-red-400 hover:bg-zinc-800 rounded-lg transition-colors"
                    title="Cancel invite"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Signal Dashboard (Founder View) */}
        <div className="mb-6">
          <TeamSignalDashboard 
            teamId={teamId} 
            teamName={team?.name}
            isAdmin={isAdmin} 
          />
        </div>

        {/* Danger Zone */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
          <h2 className="text-lg font-medium text-red-400 mb-4">Danger Zone</h2>
          <div className="space-y-3">
            {!isCreator && (
              <button
                onClick={() => setShowLeaveConfirm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Leave Team
              </button>
            )}
            {isCreator && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                data-testid="delete-team-btn"
                className="flex items-center gap-2 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete Team
              </button>
            )}
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/70" onClick={() => setShowDeleteConfirm(false)} />
            <div className="relative bg-zinc-900 rounded-xl border border-zinc-800 p-6 max-w-md w-full">
              <h3 className="text-xl font-semibold text-white mb-2">Delete Team?</h3>
              <p className="text-zinc-400 mb-6">
                This will permanently delete "{team?.name}" and all team data. This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteTeam}
                  disabled={saving}
                  data-testid="confirm-delete-team"
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white rounded-lg flex items-center justify-center gap-2"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Leave Confirmation Modal */}
        {showLeaveConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/70" onClick={() => setShowLeaveConfirm(false)} />
            <div className="relative bg-zinc-900 rounded-xl border border-zinc-800 p-6 max-w-md w-full">
              <h3 className="text-xl font-semibold text-white mb-2">Leave Team?</h3>
              <p className="text-zinc-400 mb-6">
                Are you sure you want to leave "{team?.name}"? You'll need to be invited again to rejoin.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowLeaveConfirm(false)}
                  className="flex-1 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLeaveTeam}
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white rounded-lg flex items-center justify-center gap-2"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
                  Leave
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Invite Modal */}
        {showInviteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/70" onClick={() => !inviteSending && setShowInviteModal(false)} />
            <div className="relative bg-zinc-900 rounded-xl border border-zinc-800 p-6 max-w-md w-full">
              {inviteSuccess ? (
                <div className="text-center py-4">
                  <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-6 h-6 text-green-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">Invite Sent!</h3>
                  <p className="text-zinc-400">
                    They'll receive an email with a link to join the team.
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                      <UserPlus className="w-5 h-5 text-red-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white">Invite to {team?.name}</h3>
                      <p className="text-zinc-500 text-sm">They'll get a 28-day free trial</p>
                    </div>
                  </div>
                  
                  <form onSubmit={handleSendInvite} className="space-y-4">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">Email *</label>
                      <input
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="colleague@company.com"
                        required
                        className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                        data-testid="invite-email-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">Name (optional)</label>
                      <input
                        type="text"
                        value={inviteName}
                        onChange={(e) => setInviteName(e.target.value)}
                        placeholder="Their name"
                        className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                        data-testid="invite-name-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">Personal message (optional)</label>
                      <textarea
                        value={inviteMessage}
                        onChange={(e) => setInviteMessage(e.target.value)}
                        placeholder="Hey, I think you'd find this useful for tracking your growth..."
                        rows={3}
                        maxLength={500}
                        className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                        data-testid="invite-message-input"
                      />
                    </div>
                    
                    {inviteError && (
                      <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                        {inviteError}
                      </div>
                    )}
                    
                    <div className="flex gap-3 pt-2">
                      <button
                        type="button"
                        onClick={() => setShowInviteModal(false)}
                        disabled={inviteSending}
                        className="flex-1 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={inviteSending || !inviteEmail}
                        data-testid="send-invite-btn"
                        className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white rounded-lg flex items-center justify-center gap-2 transition-colors"
                      >
                        {inviteSending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                        Send Invite
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default TeamDetailPage;
